import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  ContentTemplate,
  CustomSelect,
  HeaderInput,
  HeaderTemplate,
  Progress,
  TableActionTypes,
  TABLE_INITIAL_STATE,
  Tabs,
  TitleHeader,
  useDataTable,
} from 'web-library';

import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { useMediaQuery, SelectChangeEvent } from '@mui/material';
import { useHistory } from 'react-router-dom';
import {
  generateAuthorityName,
  NOTIFICATION_FUNCTION_MAP,
} from 'web-library/models/AuthorityFunctions';
import {
  FunctionModule,
  PermissionType,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import { configurationTypeData } from '../../../modules/admin/logic/dropDownOption';
import NotificationTemplateDataTable from './components/NotificationTemplateDataTable';
import { useGetNotificationFeature } from '../../../api-refactored/manage-notifications/useGetNotificationFeature';

import RouteKey from '../../../routes/key';
import { usePostNotificationListing } from '../../../api-refactored/manage-notifications/usePostNotificationListing';
import NotificationDataTable from './components/NotificationDataTable';
import { useGetUserAuthorities } from '../../../api-refactored/user/useGetUserAuthorities';

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  select: {
    marginBottom: theme.spacing(1.5),
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('md')]: {
      display: 'block',
      marginBottom: theme.spacing(3),
    },
  },
  button: {
    [theme.breakpoints.down('sm')]: {
      height: 'fit-content',
    },
  },
  dataTable: {
    marginBottom: theme.spacing(8),
  },
  titleDescription: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': '3',
    '-webkit-box-orient': 'vertical',
    maxWidth: '18ch',
    wordWrap: 'break-word',
    fontSize: '1rem',
    whiteSpace: 'pre-wrap',
  },
  updatedByText: {
    color: theme.color.text.textTertiary,
    fontSize: '1rem',
  },
  viewDetailsButton: {
    padding: 0,
    justifyContent: 'flex-start',
  },
  iconButtonContainer: {
    display: 'inline-flex',
  },
  mailButtonContainer: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down(1482)]: {
      marginBottom: theme.spacing(4),
    },
  },
  useTemplateIconContainer: {
    flexWrap: 'unset',
  },
  useTemplateLabel: {
    whiteSpace: 'nowrap',
  },
  editButtonContainer: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down(916)]: {
      marginBottom: theme.spacing(3),
    },
  },
  iconButton: {
    margin: 'auto',
    paddingTop: 0,
    padding: 10,
    minWidth: 0,
  },
  tabsHeader: {
    '& .MuiTab-root': {
      textTransform: 'none',
    },
    marginBottom: theme.spacing(4),
    '& button': {
      maxWidth: '326px',
    },
  },
}));

const { admin } = RouteKey.child;
const { manageNotifications, createEmailTemplate, createSMSTemplate } = RouteKey.admin.child;

const selectFeatureSubPath = (feature: string) => {
  if (feature === 'EMAIL') {
    return createEmailTemplate.route;
  }
  if (feature === 'SMS') {
    return createSMSTemplate.route;
  }
  return null;
};

const ManageNotificationsListing = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const history = useHistory<{
    featureValue: string;
    defaultTab?: number;
  }>();

  const dataFromCreateNotificationOrTemplate = history?.location?.state;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [featureValue, setFeatureValue] = useState<string>(
    dataFromCreateNotificationOrTemplate?.featureValue || ''
  );

  const [featureDisplay, setFeatureDisplay] = useState<string>('');

  const authorities = useGetUserAuthorities();

  const defaultTab = dataFromCreateNotificationOrTemplate?.defaultTab || 0;

  const tabs = [
    t('announcementsNotifications->>Listing.FeatureNotifications', {
      feature: featureDisplay,
    }),
    t('announcementsNotifications->>Listing.FeatureTemplate', {
      feature: featureDisplay,
    }),
  ];

  const [selectedTab, setSelectedTab] = useState<number>(defaultTab);
  const onChangeTab = (_event: unknown, newValue: number) => {
    setSelectedTab(newValue);
  };

  const {
    data: notificationFeature,
    isLoading: isLoadingNotificationFeature,
  } = useGetNotificationFeature({
    onSuccess: (res) => {
      if (!featureValue) {
        setFeatureValue(res[0].code);
      }
    },
  });

  const onFeatureChangedHandler = (
    event: SelectChangeEvent<unknown>
  ) => setFeatureValue(event.target.value as string);

  const { state, dispatch: tableDispatch } = useDataTable({
    ...TABLE_INITIAL_STATE,
    page: 0,
    size: 5,
    sortColumn: 'sendDate',
    order: 'desc',
    search: featureValue,
  });

  useEffect(() => {
    if (featureValue === 'EMAIL') {
      setFeatureDisplay('Email');
    } else if (featureValue === 'IN_APP') {
      setFeatureDisplay('In-App');
    } else {
      setFeatureDisplay(featureValue);
    }
  }, [featureValue]);

  const { mutate: mutatePostNotificationListing } = usePostNotificationListing({
    onSuccess: (data) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.content,
          count: data.totalElements,
        },
      } as TableActionTypes);
    },
  });

  useEffect(() => {
    if (featureValue) {
      mutatePostNotificationListing({
        apiPathParam: {
          page: state.page,
          pageSize: state.size,
          sort: state.sortColumn,
          order: state.order,
        },
        notificationType: featureValue,
      });
    }
  }, [
    mutatePostNotificationListing,
    state.order,
    state.page,
    state.size,
    state.sortColumn,
    featureValue,
  ]);

  const handleCreateTemplate = () =>
    history.push(`/${admin}/${manageNotifications.route}/${selectFeatureSubPath(featureValue)}`, {
      featureValue,
    });

  if (isLoadingNotificationFeature) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <>
      <TitleHeader title={t('announcementsNotifications->>Listing.ManageNotifications')} />
      <HeaderTemplate>
        <HeaderInput
          label={t('announcementsNotifications->>Listing.Feature')}
          input={
            notificationFeature &&
            configurationTypeData(notificationFeature) && (
              <CustomSelect
                id="inputSelect-feature"
                isHeader
                menuItems={configurationTypeData(notificationFeature)}
                value={featureValue}
                onChange={onFeatureChangedHandler}
                className={classes.select}
              />
            )
          }
          topBottom={false}
        />
      </HeaderTemplate>
      <ContentTemplate type="full">
        <Box className={classes.headerContainer}>
          <Tabs
            value={selectedTab}
            indicatorColor="primary"
            textColor="primary"
            onChange={onChangeTab}
            ariaLabel="manageNotificationTabs"
            className={classes.tabsHeader}
            tabs={tabs}
          />

          {authorities.data?.includes(
            generateAuthorityName(
              'CP',
              FunctionModule.ADMIN,
              NOTIFICATION_FUNCTION_MAP[featureValue],
              PermissionType.CREATE
            )
          ) && (
              <Button
                className={classes.button}
                fullWidth={isMobile}
                onClick={handleCreateTemplate}
                id="button-new-notification-broadcast-message-faq"
              >
                {t('announcementsNotifications->>Listing.CreateTemplate')}
              </Button>
            )}
        </Box>
        {selectedTab === 0 && (
          <NotificationDataTable featureValue={featureValue} featureDisplay={featureDisplay} />
        )}

        {selectedTab === 1 && (
          <NotificationTemplateDataTable
            featureValue={featureValue}
            featureDisplay={featureDisplay}
          />
        )}
      </ContentTemplate>
    </>
  );
};

export default ManageNotificationsListing;
